import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://www.museshaircwb.com';

const schemaJsonLd = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebPage",
      "@id": `${SITE_URL}/qualifications#webpage`,
      "url": `${SITE_URL}/qualifications`,
      "name": "Qualifications | Muses Hair CWB - Professional Certifications & Training Causeway Bay",
      "description": "Professional credentials and certifications. 20+ years hair styling experience, certified scalp therapist, advanced training in hair and scalp problem solutions in Hong Kong.",
      "inLanguage": "en",
      "isPartOf": {
        "@type": "WebSite",
        "@id": `${SITE_URL}/#website`
      },
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": SITE_URL
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Qualifications",
            "item": `${SITE_URL}/qualifications`
          }
        ]
      }
    },
    {
      "@type": "Person",
      "@id": `${SITE_URL}/qualifications#person`,
      "name": "Muses Hair CWB Stylist",
      "jobTitle": "Professional Hair Stylist & Certified Scalp Therapist",
      "hasCredential": {
        "@type": "ItemList",
        "itemListElement": [
          {
            "@type": "EducationalOccupationalCredential",
            "position": 1,
            "name": "Certified Scalp Therapist",
            "description": "Advanced certification in scalp analysis, diagnosis, and therapeutic treatment protocols"
          },
          {
            "@type": "EducationalOccupationalCredential",
            "position": 2,
            "name": "Professional Hairdresser Certification",
            "description": "Comprehensive professional hairdressing certification covering cutting, coloring, and styling"
          },
          {
            "@type": "EducationalOccupationalCredential",
            "position": 3,
            "name": "Advanced Color Specialist",
            "description": "Specialized training in color theory, formulation, and application techniques"
          },
          {
            "@type": "EducationalOccupationalCredential",
            "position": 4,
            "name": "Keratin Treatment Expert",
            "description": "Certified in advanced keratin smoothing systems and hair repair treatments"
          },
          {
            "@type": "EducationalOccupationalCredential",
            "position": 5,
            "name": "Hair Loss Specialist",
            "description": "Specialized training in hair thinning analysis and treatment protocols"
          }
        ]
      },
      "worksFor": {
        "@type": "HairSalon",
        "@id": `${SITE_URL}/#hairsalon`,
        "name": "Muses Hair CWB"
      }
    }
  ]
};

export default function QualificationsPage() {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const scriptId = 'schema-jsonld-qualifications';
    let script = document.getElementById(scriptId) as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement('script');
      script.id = scriptId;
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(schemaJsonLd);
    return () => {
      const el = document.getElementById(scriptId);
      if (el) el.remove();
    };
  }, []);

  const scrollToContact = () => {
    window.REACT_APP_NAVIGATE('/contact');
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-[#F3F0EB]/98 backdrop-blur-sm shadow-sm' : 'bg-[#F3F0EB]'
      }`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center">
            <Link to="/">
              <img 
                src="https://www.museshaircwb.com/wp-content/uploads/2024/01/cropped-Muses-Hair-Logo-1.png" 
                alt="Muses Hair CWB" 
                className="h-11 w-auto cursor-pointer"
              />
            </Link>
          </div>
          <div className="hidden md:flex items-center gap-8">
            <Link to="/" className="text-sm font-medium text-gray-800 hover:text-gray-600 transition-colors">Home</Link>
            <Link to="/about" className="text-sm font-medium text-gray-800 hover:text-gray-600 transition-colors">About</Link>
            <Link to="/qualifications" className="text-sm font-medium text-gray-900 transition-colors">Qualifications</Link>
            <Link to="/services" className="text-sm font-medium text-gray-800 hover:text-gray-600 transition-colors">Services</Link>
            <Link to="/products" className="text-sm font-medium text-gray-800 hover:text-gray-600 transition-colors">Products</Link>
            <Link to="/testimonials" className="text-sm font-medium text-gray-800 hover:text-gray-600 transition-colors">Testimonials</Link>
            <Link to="/contact" className="text-sm font-medium text-gray-800 hover:text-gray-600 transition-colors">Contact</Link>
          </div>
          <button onClick={scrollToContact} className="bg-gray-800 hover:bg-gray-700 text-white px-6 py-2.5 rounded-md text-sm font-medium transition-all whitespace-nowrap cursor-pointer">
            Book Consultation
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6 bg-[#F3F0EB]">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-block border border-gray-400 text-gray-700 px-4 py-1.5 rounded-full text-xs uppercase tracking-wider mb-8">
            Professional Credentials
          </div>
          <h1 className="font-serif text-5xl md:text-6xl text-gray-900 mb-6 leading-tight" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Qualifications &<br />Professional Training
          </h1>
          <p className="text-gray-700 text-lg leading-relaxed max-w-2xl mx-auto">
            Two decades of continuous learning, advanced certifications, and hands-on experience serving international clients across diverse hair types and scalp conditions.
          </p>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-serif text-4xl md:text-5xl text-gray-900 mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Professional Journey
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              A timeline of continuous growth, learning, and expertise development
            </p>
          </div>

          <div className="relative">
            {/* Timeline Line */}
            <div className="absolute left-1/2 transform -translate-x-1/2 w-0.5 h-full bg-gray-200 hidden md:block"></div>

            {/* Timeline Items */}
            <div className="space-y-12">
              {/* 2024 - Present */}
              <div className="relative grid md:grid-cols-2 gap-8 items-center">
                <div className="md:text-right">
                  <div className="inline-block bg-gray-800 text-white px-4 py-1.5 rounded-full text-sm font-medium mb-4">
                    2024 - Present
                  </div>
                  <h3 className="font-serif text-2xl text-gray-900 mb-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                    Integrated Hair & Scalp Specialist
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    Operating Muses Hair CWB as a comprehensive solution center, combining advanced scalp therapy with expert styling for international and local clients.
                  </p>
                </div>
                <div className="hidden md:block"></div>
                <div className="absolute left-1/2 transform -translate-x-1/2 w-4 h-4 bg-gray-800 rounded-full hidden md:block"></div>
              </div>

              {/* 2020 - 2023 */}
              <div className="relative grid md:grid-cols-2 gap-8 items-center">
                <div className="hidden md:block"></div>
                <div>
                  <div className="inline-block bg-gray-800 text-white px-4 py-1.5 rounded-full text-sm font-medium mb-4">
                    2020 - 2023
                  </div>
                  <h3 className="font-serif text-2xl text-gray-900 mb-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                    Advanced Scalp Therapy Certification
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    Completed comprehensive scalp therapy training program, including scalp analysis, diagnostic techniques, therapeutic treatments, and clinical protocols for various scalp conditions.
                  </p>
                </div>
                <div className="absolute left-1/2 transform -translate-x-1/2 w-4 h-4 bg-gray-800 rounded-full hidden md:block"></div>
              </div>

              {/* 2015 - 2020 */}
              <div className="relative grid md:grid-cols-2 gap-8 items-center">
                <div className="md:text-right">
                  <div className="inline-block bg-gray-800 text-white px-4 py-1.5 rounded-full text-sm font-medium mb-4">
                    2015 - 2020
                  </div>
                  <h3 className="font-serif text-2xl text-gray-900 mb-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                    Specialized Training Programs
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    Advanced courses in keratin treatments, hair repair systems, color correction techniques, and specialized care for diverse hair textures including European, Asian, and textured hair types.
                  </p>
                </div>
                <div className="hidden md:block"></div>
                <div className="absolute left-1/2 transform -translate-x-1/2 w-4 h-4 bg-gray-800 rounded-full hidden md:block"></div>
              </div>

              {/* 2010 - 2015 */}
              <div className="relative grid md:grid-cols-2 gap-8 items-center">
                <div className="hidden md:block"></div>
                <div>
                  <div className="inline-block bg-gray-800 text-white px-4 py-1.5 rounded-full text-sm font-medium mb-4">
                    2010 - 2015
                  </div>
                  <h3 className="font-serif text-2xl text-gray-900 mb-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                    International Clientele Development
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    Built reputation serving expats and international professionals in Hong Kong, developing expertise in cross-cultural communication and understanding diverse hair care needs and preferences.
                  </p>
                </div>
                <div className="absolute left-1/2 transform -translate-x-1/2 w-4 h-4 bg-gray-800 rounded-full hidden md:block"></div>
              </div>

              {/* 2005 - 2010 */}
              <div className="relative grid md:grid-cols-2 gap-8 items-center">
                <div className="md:text-right">
                  <div className="inline-block bg-gray-800 text-white px-4 py-1.5 rounded-full text-sm font-medium mb-4">
                    2005 - 2010
                  </div>
                  <h3 className="font-serif text-2xl text-gray-900 mb-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                    Advanced Styling Mastery
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    Refined cutting and coloring techniques through continuous education, workshops with industry leaders, and hands-on experience with thousands of clients across diverse backgrounds.
                  </p>
                </div>
                <div className="hidden md:block"></div>
                <div className="absolute left-1/2 transform -translate-x-1/2 w-4 h-4 bg-gray-800 rounded-full hidden md:block"></div>
              </div>

              {/* 2000 - 2005 */}
              <div className="relative grid md:grid-cols-2 gap-8 items-center">
                <div className="hidden md:block"></div>
                <div>
                  <div className="inline-block bg-gray-800 text-white px-4 py-1.5 rounded-full text-sm font-medium mb-4">
                    2000 - 2005
                  </div>
                  <h3 className="font-serif text-2xl text-gray-900 mb-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                    Professional Foundation
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    Completed professional hairdressing certification and began career in Hong Kong's premium salon industry, establishing fundamental skills in cutting, coloring, and client consultation.
                  </p>
                </div>
                <div className="absolute left-1/2 transform -translate-x-1/2 w-4 h-4 bg-gray-800 rounded-full hidden md:block"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Certifications Section */}
      <section className="py-20 px-6 bg-[#F3F0EB]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-serif text-4xl md:text-5xl text-gray-900 mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Professional Certifications
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Recognized credentials demonstrating expertise and commitment to excellence
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-lg p-8 text-center hover:shadow-lg transition-all">
              <div className="w-20 h-20 flex items-center justify-center bg-gray-800 rounded-full mx-auto mb-6">
                <i className="ri-award-line text-4xl text-white"></i>
              </div>
              <h3 className="font-serif text-2xl text-gray-900 mb-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Certified Scalp Therapist
              </h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                Advanced certification in scalp analysis, diagnosis, and therapeutic treatment protocols for various scalp conditions.
              </p>
              <div className="inline-block bg-[#F3F0EB] text-gray-700 px-4 py-2 rounded-full text-sm">
                Clinical Expertise
              </div>
            </div>

            <div className="bg-white rounded-lg p-8 text-center hover:shadow-lg transition-all">
              <div className="w-20 h-20 flex items-center justify-center bg-gray-800 rounded-full mx-auto mb-6">
                <i className="ri-scissors-cut-line text-4xl text-white"></i>
              </div>
              <h3 className="font-serif text-2xl text-gray-900 mb-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Professional Hairdresser
              </h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                Comprehensive professional hairdressing certification covering cutting, coloring, styling, and client consultation techniques.
              </p>
              <div className="inline-block bg-[#F3F0EB] text-gray-700 px-4 py-2 rounded-full text-sm">
                Styling Mastery
              </div>
            </div>

            <div className="bg-white rounded-lg p-8 text-center hover:shadow-lg transition-all">
              <div className="w-20 h-20 flex items-center justify-center bg-gray-800 rounded-full mx-auto mb-6">
                <i className="ri-flask-line text-4xl text-white"></i>
              </div>
              <h3 className="font-serif text-2xl text-gray-900 mb-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Advanced Color Specialist
              </h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                Specialized training in color theory, formulation, application techniques, and color correction for all hair types.
              </p>
              <div className="inline-block bg-[#F3F0EB] text-gray-700 px-4 py-2 rounded-full text-sm">
                Color Expertise
              </div>
            </div>

            <div className="bg-white rounded-lg p-8 text-center hover:shadow-lg transition-all">
              <div className="w-20 h-20 flex items-center justify-center bg-gray-800 rounded-full mx-auto mb-6">
                <i className="ri-drop-line text-4xl text-white"></i>
              </div>
              <h3 className="font-serif text-2xl text-gray-900 mb-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Keratin Treatment Expert
              </h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                Certified in advanced keratin smoothing systems and hair repair treatments for damaged and frizzy hair.
              </p>
              <div className="inline-block bg-[#F3F0EB] text-gray-700 px-4 py-2 rounded-full text-sm">
                Treatment Specialist
              </div>
            </div>

            <div className="bg-white rounded-lg p-8 text-center hover:shadow-lg transition-all">
              <div className="w-20 h-20 flex items-center justify-center bg-gray-800 rounded-full mx-auto mb-6">
                <i className="ri-heart-pulse-line text-4xl text-white"></i>
              </div>
              <h3 className="font-serif text-2xl text-gray-900 mb-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Hair Loss Specialist
              </h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                Specialized training in hair thinning analysis, scalp health assessment, and targeted treatment protocols.
              </p>
              <div className="inline-block bg-[#F3F0EB] text-gray-700 px-4 py-2 rounded-full text-sm">
                Problem Solving
              </div>
            </div>

            <div className="bg-white rounded-lg p-8 text-center hover:shadow-lg transition-all">
              <div className="w-20 h-20 flex items-center justify-center bg-gray-800 rounded-full mx-auto mb-6">
                <i className="ri-global-line text-4xl text-white"></i>
              </div>
              <h3 className="font-serif text-2xl text-gray-900 mb-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                International Hair Expert
              </h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                Extensive experience and training in diverse hair types including European, Asian, African, and textured hair.
              </p>
              <div className="inline-block bg-[#F3F0EB] text-gray-700 px-4 py-2 rounded-full text-sm">
                Cultural Expertise
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Specialized Skills Section */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-serif text-4xl md:text-5xl text-gray-900 mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Specialized Skills & Techniques
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Advanced expertise developed through years of continuous learning and hands-on practice
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-[#F9F8F6] rounded-lg p-8">
              <h3 className="font-serif text-2xl text-gray-900 mb-6" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Clinical Scalp Expertise
              </h3>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <i className="ri-check-line text-gray-600 text-xl mt-0.5"></i>
                  <div>
                    <strong className="text-gray-900">Scalp Analysis & Diagnosis</strong>
                    <p className="text-gray-600 text-sm mt-1">Professional assessment of scalp conditions using clinical observation and diagnostic techniques</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-check-line text-gray-600 text-xl mt-0.5"></i>
                  <div>
                    <strong className="text-gray-900">Therapeutic Treatment Protocols</strong>
                    <p className="text-gray-600 text-sm mt-1">Customized treatment plans for oily scalp, dandruff, sensitivity, and inflammation</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-check-line text-gray-600 text-xl mt-0.5"></i>
                  <div>
                    <strong className="text-gray-900">Hair Loss Management</strong>
                    <p className="text-gray-600 text-sm mt-1">Targeted approaches for thinning hair, promoting scalp health and hair growth</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-check-line text-gray-600 text-xl mt-0.5"></i>
                  <div>
                    <strong className="text-gray-900">Scalp Detoxification</strong>
                    <p className="text-gray-600 text-sm mt-1">Deep cleansing treatments to remove buildup and restore scalp balance</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-[#F9F8F6] rounded-lg p-8">
              <h3 className="font-serif text-2xl text-gray-900 mb-6" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Advanced Styling Techniques
              </h3>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <i className="ri-check-line text-gray-600 text-xl mt-0.5"></i>
                  <div>
                    <strong className="text-gray-900">Precision Cutting</strong>
                    <p className="text-gray-600 text-sm mt-1">Expert techniques for all hair types, textures, and lengths with attention to face shape and lifestyle</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-check-line text-gray-600 text-xl mt-0.5"></i>
                  <div>
                    <strong className="text-gray-900">Color Formulation & Application</strong>
                    <p className="text-gray-600 text-sm mt-1">Custom color creation, balayage, highlights, and color correction for natural-looking results</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-check-line text-gray-600 text-xl mt-0.5"></i>
                  <div>
                    <strong className="text-gray-900">Texture Transformation</strong>
                    <p className="text-gray-600 text-sm mt-1">Keratin smoothing, perming, and texture control for manageable, beautiful hair</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-check-line text-gray-600 text-xl mt-0.5"></i>
                  <div>
                    <strong className="text-gray-900">Hair Repair Systems</strong>
                    <p className="text-gray-600 text-sm mt-1">Deep conditioning, protein treatments, and bond repair for damaged hair restoration</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-[#F9F8F6] rounded-lg p-8">
              <h3 className="font-serif text-2xl text-gray-900 mb-6" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Cultural Hair Expertise
              </h3>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <i className="ri-check-line text-gray-600 text-xl mt-0.5"></i>
                  <div>
                    <strong className="text-gray-900">European Fine Hair</strong>
                    <p className="text-gray-600 text-sm mt-1">Specialized techniques for volume, texture, and color on delicate hair structures</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-check-line text-gray-600 text-xl mt-0.5"></i>
                  <div>
                    <strong className="text-gray-900">Asian Thick Hair</strong>
                    <p className="text-gray-600 text-sm mt-1">Expert handling of dense, straight hair with focus on shape, movement, and manageability</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-check-line text-gray-600 text-xl mt-0.5"></i>
                  <div>
                    <strong className="text-gray-900">Textured & Curly Hair</strong>
                    <p className="text-gray-600 text-sm mt-1">Understanding of curl patterns, moisture needs, and specialized cutting techniques</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-check-line text-gray-600 text-xl mt-0.5"></i>
                  <div>
                    <strong className="text-gray-900">Mixed Heritage Hair</strong>
                    <p className="text-gray-600 text-sm mt-1">Customized approaches for unique hair characteristics and diverse texture combinations</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-[#F9F8F6] rounded-lg p-8">
              <h3 className="font-serif text-2xl text-gray-900 mb-6" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Client Care Excellence
              </h3>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <i className="ri-check-line text-gray-600 text-xl mt-0.5"></i>
                  <div>
                    <strong className="text-gray-900">Comprehensive Consultation</strong>
                    <p className="text-gray-600 text-sm mt-1">In-depth assessment of hair history, concerns, lifestyle, and goals</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-check-line text-gray-600 text-xl mt-0.5"></i>
                  <div>
                    <strong className="text-gray-900">Personalized Treatment Plans</strong>
                    <p className="text-gray-600 text-sm mt-1">Custom protocols combining therapeutic care with aesthetic goals</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-check-line text-gray-600 text-xl mt-0.5"></i>
                  <div>
                    <strong className="text-gray-900">Cross-Cultural Communication</strong>
                    <p className="text-gray-600 text-sm mt-1">Understanding diverse beauty standards, preferences, and cultural sensitivities</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-check-line text-gray-600 text-xl mt-0.5"></i>
                  <div>
                    <strong className="text-gray-900">Ongoing Support & Education</strong>
                    <p className="text-gray-600 text-sm mt-1">Home care guidance, product recommendations, and maintenance advice</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Experience Stats Section */}
      <section className="py-20 px-6 bg-[#F3F0EB]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-serif text-4xl md:text-5xl text-gray-900 mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Experience by the Numbers
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Two decades of dedication, learning, and client care
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            <div className="bg-white rounded-lg p-8 text-center">
              <div className="text-5xl font-bold text-gray-900 mb-2">20+</div>
              <div className="text-sm text-gray-600 uppercase tracking-wider mb-3">Years Experience</div>
              <p className="text-gray-600 text-sm leading-relaxed">
                Continuous practice and refinement of skills since 2000
              </p>
            </div>

            <div className="bg-white rounded-lg p-8 text-center">
              <div className="text-5xl font-bold text-gray-900 mb-2">1000+</div>
              <div className="text-sm text-gray-600 uppercase tracking-wider mb-3">Clients Served</div>
              <p className="text-gray-600 text-sm leading-relaxed">
                Diverse clientele from over 30 countries worldwide
              </p>
            </div>

            <div className="bg-white rounded-lg p-8 text-center">
              <div className="text-5xl font-bold text-gray-900 mb-2">15+</div>
              <div className="text-sm text-gray-600 uppercase tracking-wider mb-3">Certifications</div>
              <p className="text-gray-600 text-sm leading-relaxed">
                Professional credentials and specialized training programs
              </p>
            </div>

            <div className="bg-white rounded-lg p-8 text-center">
              <div className="text-5xl font-bold text-gray-900 mb-2">100%</div>
              <div className="text-sm text-gray-600 uppercase tracking-wider mb-3">Commitment</div>
              <p className="text-gray-600 text-sm leading-relaxed">
                Dedicated to continuous learning and client satisfaction
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Continuous Learning Section */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <div className="aspect-[4/5] rounded-lg overflow-hidden">
                <img 
                  src="https://public.readdy.ai/ai/img_res/82e75d04515deb8fd94940a2dd4f1287.jpg" 
                  alt="Continuous Professional Learning" 
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
            <div>
              <div className="inline-block border border-gray-400 text-gray-700 px-4 py-1.5 rounded-full text-xs uppercase tracking-wider mb-8">
                Lifelong Learning
              </div>
              <h2 className="font-serif text-4xl md:text-5xl text-gray-900 mb-6 leading-tight" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Committed to<br />Continuous Growth
              </h2>
              <p className="text-gray-700 text-lg leading-relaxed mb-6">
                The beauty industry evolves constantly, with new techniques, products, and research emerging regularly. I believe that true expertise requires lifelong learning and adaptation.
              </p>
              <p className="text-gray-700 text-lg leading-relaxed mb-6">
                I regularly attend workshops, training programs, and industry conferences to stay current with the latest advances in hair care, scalp therapy, and styling techniques.
              </p>
              <p className="text-gray-700 text-lg leading-relaxed mb-8">
                This commitment ensures that every client benefits from the most effective, up-to-date approaches available in the industry today.
              </p>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <i className="ri-check-line text-gray-600 text-xl mt-0.5"></i>
                  <span className="text-gray-700">Regular attendance at international beauty conferences</span>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-check-line text-gray-600 text-xl mt-0.5"></i>
                  <span className="text-gray-700">Ongoing training with premium product brands</span>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-check-line text-gray-600 text-xl mt-0.5"></i>
                  <span className="text-gray-700">Study of latest scalp health research and clinical findings</span>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-check-line text-gray-600 text-xl mt-0.5"></i>
                  <span className="text-gray-700">Collaboration with industry experts and specialists</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-[#F3F0EB]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-serif text-4xl md:text-5xl text-gray-900 mb-6" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Experience the Difference<br />Expertise Makes
          </h2>
          <p className="text-gray-600 text-lg mb-10 max-w-2xl mx-auto">
            Book a consultation to benefit from two decades of professional training, advanced certifications, and hands-on experience solving real hair and scalp problems.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a 
              href="https://wa.me/message/YAZZ2YL77SHLN1" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#20BA5A] text-white px-8 py-3.5 rounded-md text-base font-medium transition-all whitespace-nowrap cursor-pointer"
            >
              <i className="ri-whatsapp-line text-xl"></i>
              WhatsApp Consultation
            </a>
            <Link 
              to="/contact"
              className="bg-gray-800 hover:bg-gray-700 text-white px-8 py-3.5 rounded-md text-base font-medium transition-all whitespace-nowrap cursor-pointer"
            >
              Book Appointment
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-6 py-16">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div>
              <img 
                src="https://www.museshaircwb.com/wp-content/uploads/2024/01/cropped-Muses-Hair-Logo-1.png" 
                alt="Muses Hair CWB" 
                className="h-11 w-auto mb-6 brightness-0 invert"
              />
              <p className="text-gray-400 text-sm leading-relaxed">
                Professional solution center for hair and scalp problems in Causeway Bay, Hong Kong.
              </p>
            </div>

            <div>
              <h4 className="text-xs uppercase tracking-wider text-gray-400 mb-6">Quick Links</h4>
              <ul className="space-y-3">
                <li><Link to="/" className="text-gray-300 hover:text-white transition-colors text-sm cursor-pointer">Home</Link></li>
                <li><Link to="/about" className="text-gray-300 hover:text-white transition-colors text-sm cursor-pointer">About</Link></li>
                <li><Link to="/qualifications" className="text-gray-300 hover:text-white transition-colors text-sm cursor-pointer">Qualifications</Link></li>
                <li><Link to="/services" className="text-gray-300 hover:text-white transition-colors text-sm cursor-pointer">Services</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="text-xs uppercase tracking-wider text-gray-400 mb-6">More</h4>
              <ul className="space-y-3">
                <li><Link to="/products" className="text-gray-300 hover:text-white transition-colors text-sm cursor-pointer">Products</Link></li>
                <li><Link to="/testimonials" className="text-gray-300 hover:text-white transition-colors text-sm cursor-pointer">Testimonials</Link></li>
                <li><Link to="/contact" className="text-gray-300 hover:text-white transition-colors text-sm cursor-pointer">Contact</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="text-xs uppercase tracking-wider text-gray-400 mb-6">Connect</h4>
              <ul className="space-y-3">
                <li><a href="https://www.instagram.com/museshaircwb" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-white transition-colors text-sm cursor-pointer">Instagram</a></li>
                <li><a href="https://www.facebook.com/museshaircwb" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-white transition-colors text-sm cursor-pointer">Facebook</a></li>
                <li><a href="https://wa.me/message/YAZZ2YL77SHLN1" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-white transition-colors text-sm cursor-pointer">WhatsApp</a></li>
              </ul>
            </div>
          </div>

          <div className="pt-8 border-t border-gray-800 text-center">
            <p className="text-gray-400 text-sm">
              © 2024 Muses Hair CWB. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}